import { createElement, lazy } from "react"
import type { FC, LazyExoticComponent, ReactElement } from "react"

import isNull from "lodash-es/isNull"

import type { TTFunction, TTranslationKeys } from "$/i18n/i18n"

import { Button, EButtonVariant } from "@/3514/components"

import {
    ECoachConferenceAccountVariant as EVariant,
    type TCoachConferenceAccount as TAccount,
    type TCoachConferenceAccountRenderConfig as TRenderConfig
} from "./coach.conference-account.types"

const ZoomIcon: LazyExoticComponent<FC> = lazy(
    (): Promise<{ default: FC }> =>
        import("./coach.conference-account.assets").then(({ CoachConferenceAccountZoomIcon: Z }): { default: FC } => ({
            default: Z
        }))
)
const MsTeamsIcon: LazyExoticComponent<FC> = lazy(
    (): Promise<{ default: FC }> =>
        import("./coach.conference-account.assets").then(
            ({ CoachConferenceAccountMsTeamsIcon: Z }): { default: FC } => ({
                default: Z
            })
        )
)

const imagesMap: { [V in EVariant]: ReactElement } = {
    [EVariant.Zoom]: createElement(ZoomIcon),
    [EVariant.MSTeams]: createElement(MsTeamsIcon)
}

const titlesMap: { [V in EVariant]: keyof TTranslationKeys } = {
    [EVariant.Zoom]: "coachSide.conferenceAccountCard.zoom.title",
    [EVariant.MSTeams]: "coachSide.conferenceAccountCard.msTeams.title"
}

function renderConfig(params: { t: TTFunction } & Pick<TAccount, "variant" | "linkUrl">): TRenderConfig {
    const _isLinked: boolean = isNull(params.linkUrl)

    switch (params.variant) {
        case EVariant.MSTeams:
            return {
                title: params.t(titlesMap[EVariant.MSTeams]),
                description: params.t("coachSide.conferenceAccountCard.msTeams.description"),
                image: imagesMap[EVariant.MSTeams],
                actionProps: _isLinked
                    ? undefined
                    : {
                          isTargetBlank: true,
                          to: params.linkUrl,
                          className: "mt-auto mb-[8px]",
                          children: createElement(
                              Button,
                              { variant: EButtonVariant.Solid, isDisabled: false },
                              params.t("coachSide.conferenceAccountCard.actionButton")
                          )
                      },
                status: _isLinked ? params.t("common.success") : undefined
            }
        case EVariant.Zoom:
        default:
            return {
                title: params.t(titlesMap[EVariant.Zoom]),
                description: params.t("coachSide.conferenceAccountCard.zoom.description"),
                image: imagesMap[EVariant.Zoom],
                actionProps: _isLinked
                    ? undefined
                    : {
                          isTargetBlank: true,
                          to: params.linkUrl,
                          className: "mt-auto mb-[8px]",
                          children: createElement(
                              Button,
                              { variant: EButtonVariant.Solid, isDisabled: false },
                              params.t("coachSide.conferenceAccountCard.actionButton")
                          )
                      },
                status: _isLinked ? params.t("common.success") : undefined
            }
    }
}

const config = { renderConfig, imagesMap, titlesMap }

export { config as coachConferenceAccountConfig }
